// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

/**
 * Role Configuration
 */

export const ROLE_MAPPING = {
  // Super Admin roles
  admin: "Super Admin",
  super_admin: "Super Admin",
  "Super Admin": "Super Admin",

  // Tenant Admin roles
  tenant_admin: "Tenant Admin",
  "Tenant Admin": "Tenant Admin",

  // Sales roles
  sales_manager: "Sales Manager",
  sales_agent: "Sales Agent",
  "Sales Agent": "Sales Agent",

  // Order processing roles
  order_processor: "Order Processor",
  "Order Processor": "Order Processor",

  // Booking roles
  booking_approver: "Booking Approver",
  "Booking Approver": "Booking Approver",

  // Billing roles
  billing_manager: "Billing Manager",
  "Billing Manager": "Billing Manager",

  // Support roles
  customer_support: "Customer Support",
  "Customer Support": "Customer Support",

  // Inventory roles
  inventory_manager: "Inventory Manager",
  "Inventory Manager": "Inventory Manager",

  // Analytics roles
  report_analyst: "Report Analyst",
  "Report Analyst": "Report Analyst",

  // Customer roles
  customer: "Customer",
  Customer: "Customer",

  // General user roles
  user: "User",

  // Special cases
  none: null,
};

/**
 * Get mapped role for a given Keycloak role
 * @param {string} keycloakRole - The role from Keycloak
 * @returns {string|null} - The mapped database role or null if not found
 */
export const getMappedRole = (keycloakRole) => {
  return (
    ROLE_MAPPING[keycloakRole] ||
    ROLE_MAPPING[keycloakRole.toLowerCase()] ||
    null
  );
};

/**
 * Map multiple Keycloak roles to database roles
 * @param {string[]} keycloakRoles - Array of Keycloak roles
 * @returns {string[]} - Array of mapped database roles (filtered to remove nulls)
 */
export const mapKeycloakRolesToDatabase = (keycloakRoles) => {
  return keycloakRoles.map(getMappedRole).filter(Boolean);
};

/**
 * Get all available database roles
 * @returns {string[]} - Array of unique database roles
 */
export const getAvailableDatabaseRoles = () => {
  return [...new Set(Object.values(ROLE_MAPPING).filter(Boolean))];
};

/**
 * Get all available Keycloak roles
 * @returns {string[]} - Array of unique Keycloak roles
 */
export const getAvailableKeycloakRoles = () => {
  return Object.keys(ROLE_MAPPING).filter((role) => role !== "none");
};

// System/technical roles that should not count as business access roles
export const SYSTEM_ROLES = [
  "none",
  "offline_access",
  "default-roles-vhp",
  "uma_authorization",
];

export const isSystemRole = (role) => SYSTEM_ROLES.includes(role);

export const filterMeaningfulRoles = (roles = []) =>
  roles.filter((role) => !isSystemRole(role));

// Add role categories for easier management
export const ROLE_CATEGORIES = {
  SUPER_ADMIN_ROLES: ["super_admin", "Super Admin", "admin", "Admin"],
  ADMIN_ROLES: ["Tenant Admin", "tenant_admin"],

  MANAGER_ROLES: [
    "Sales Manager",
    "Inventory Manager",
    "Billing Manager",
    "Report Analyst",
  ],

  OPERATIONAL_ROLES: [
    "Sales Agent",
    "Order Processor",
    "Booking Approver",
    "Customer Support",
    "gdpr_officer",
    "gdpr_manager",
    "gdpr_admin",
    "gdpr_user",
    "gdpr_officer",
    "gdpr_manager",
    "gdpr_admin",
    "gdpr_user",
    "gdpr_manager",
  ],

  USER_ROLES: ["Customer", "User", "user"],
};

/**
 * Roles that can see all tenants' data (dashboard, bookings, users, etc.)
 * Only Super Admin — Tenant Admin must see only their own tenant.
 */
const SUPER_ADMIN_ONLY_ROLES = ["admin", "super_admin", "Super Admin"];

/**
 * True only for Super Admin. Use for: tenant selector, "All Tenants", cross-tenant APIs.
 * Tenant Admins must not get this — they see only their own tenant's data.
 */
export const hasSuperAdminAccess = (userRoles) => {
  return userRoles.some(
    (role) =>
      SUPER_ADMIN_ONLY_ROLES.includes(role) ||
      getMappedRole(role) === "Super Admin",
  );
};

/**
 * True if user is Tenant Admin (can only view/manage their own tenant's data).
 */
export const hasTenantAdminAccess = (userRoles) => {
  return userRoles.some(
    (role) =>
      role === "Tenant Admin" ||
      role === "tenant_admin" ||
      getMappedRole(role) === "Tenant Admin",
  );
};

export const hasAdminAccess = (userRoles) => {
  // Super Admin always has admin access
  if (hasSuperAdminAccess(userRoles)) {
    return true;
  }

  return userRoles.some(
    (role) =>
      ROLE_CATEGORIES.ADMIN_ROLES.includes(role) ||
      ROLE_CATEGORIES.MANAGER_ROLES.includes(role) ||
      ROLE_CATEGORIES.OPERATIONAL_ROLES.includes(role),
  );
};
// Helper function to check if user has specific role category
export const hasRoleCategory = (userRoles, category) => {
  return userRoles.some((role) => ROLE_CATEGORIES[category]?.includes(role));
};

export const MAP_EDITOR_ROLES = [
  ...ROLE_CATEGORIES.SUPER_ADMIN_ROLES,
  ...ROLE_CATEGORIES.ADMIN_ROLES,
  ...ROLE_CATEGORIES.MANAGER_ROLES,
];

export const hasMapEditorAccess = (userRoles = []) =>
  hasSuperAdminAccess(userRoles) ||
  hasTenantAdminAccess(userRoles) ||
  hasRoleCategory(userRoles, "MANAGER_ROLES");
